import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Chip,
  Typography,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert
} from '@mui/material';
import { Add as AddIcon, LocalOffer as TagIcon } from '@mui/icons-material';
import { supabase } from '../utils/supabase';
import TagChip from './TagChip';

const PREDEFINED_COLORS = [
  '#1976d2', // Blue
  '#388e3c', // Green  
  '#f57c00', // Orange
  '#d32f2f', // Red
  '#7b1fa2', // Purple
  '#1976d2', // Teal
  '#795548', // Brown
  '#607d8b', // Blue Grey
];

/**
 * Dialog for managing tags on a recipe
 */
export default function RecipeTagDialog({ 
  open, 
  onClose, 
  recipe, 
  onTagsUpdated,
  session
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [recipeTags, setRecipeTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState(PREDEFINED_COLORS[0]);
  const [selectedTag, setSelectedTag] = useState(null);

  // Load available tags and recipe tags when dialog opens
  useEffect(() => {
    if (open && recipe) {
      loadAvailableTags();
      loadRecipeTags();
    }
  }, [open, recipe]);

  const loadAvailableTags = async () => {
    try {
      // Use direct REST API call to avoid session hanging issues
      const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/recipe_tags?order=name.asc`;
      
      // Create a timeout promise to race against the query
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          console.log('🏷️ Available tags query timeout after 5 seconds');
          reject(new Error('Tags query timeout'));
        }, 5000);
      });
      
      const fetchPromise = fetch(url, {
        method: 'GET',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const response = await Promise.race([fetchPromise, timeoutPromise]);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      setAvailableTags(data || []);
    } catch (err) {
      console.error('Error loading tags:', err);
      setError('Failed to load available tags');
    }
  };

  const loadRecipeTags = async () => {
    try {
      // Use direct REST API call to avoid session hanging issues
      const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/recipe_tag_assignments?recipe_id=eq.${recipe.id}&select=tag_id,recipe_tags(id,name,color)`;
      
      // Create a timeout promise to race against the query
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          console.log('🏷️ Recipe tags query timeout after 5 seconds');
          reject(new Error('Recipe tags query timeout'));
        }, 5000);
      });
      
      const fetchPromise = fetch(url, {
        method: 'GET',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const response = await Promise.race([fetchPromise, timeoutPromise]);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      const tags = data?.map(assignment => assignment.recipe_tags) || [];
      setRecipeTags(tags);
    } catch (err) {
      console.error('Error loading recipe tags:', err);
      setError('Failed to load recipe tags');
    }
  };

  const createNewTag = async () => {
    if (!newTagName.trim()) return;

    try {
      setLoading(true);
      setError(null);

      // Use direct REST API call to avoid session hanging issues
      const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/recipe_tags`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify({
          name: newTagName.trim(),
          color: newTagColor,
          user_id: session?.user?.id
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const newTag = Array.isArray(data) ? data[0] : data;

      setAvailableTags(prev => [...prev, newTag]);
      setNewTagName('');
      
      // Automatically add the new tag to the recipe
      await addTagToRecipe(newTag);
      
    } catch (err) {
      console.error('Error creating tag:', err);
      setError('Failed to create tag');
    } finally {
      setLoading(false);
    }
  };

  const addTagToRecipe = async (tag) => {
    try {
      setLoading(true);
      setError(null);

      // Check if tag is already assigned
      if (recipeTags.some(t => t.id === tag.id)) {
        return;
      }

      // Use direct REST API call to avoid session hanging issues
      const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/recipe_tag_assignments`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipe_id: recipe.id,
          tag_id: tag.id
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      setRecipeTags(prev => [...prev, tag]);
      onTagsUpdated?.();
      
    } catch (err) {
      console.error('Error adding tag to recipe:', err);
      setError('Failed to add tag to recipe');
    } finally {
      setLoading(false);
    }
  };

  const removeTagFromRecipe = async (tagId) => {
    try {
      setLoading(true);
      setError(null);

      // Use direct REST API call to avoid session hanging issues
      const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/recipe_tag_assignments?recipe_id=eq.${recipe.id}&tag_id=eq.${tagId}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

            setRecipeTags(prev => prev.filter(tag => tag.id !== tagId));
      onTagsUpdated?.();
      
    } catch (err) {
      console.error('Error removing tag from recipe:', err);
      setError('Failed to remove tag from recipe');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewTagName('');
    setSelectedTag(null);
    setError(null);
    onClose();
  };

  const unassignedTags = availableTags.filter(
    tag => !recipeTags.some(recipeTag => recipeTag.id === tag.id)
  );

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <TagIcon sx={{ mr: 1 }} />
        Manage Tags for "{recipe?.title}"
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Current Recipe Tags */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Current Tags
          </Typography>
          {recipeTags.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {recipeTags.map(tag => (
                <TagChip
                  key={tag.id}
                  tag={tag}
                  showDelete={true}
                  onDelete={removeTagFromRecipe}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No tags assigned yet
            </Typography>
          )}
        </Box>

        {/* Add Existing Tag */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Add Existing Tag
          </Typography>
          <Autocomplete
            options={unassignedTags}
            getOptionLabel={(option) => option.name}
            value={selectedTag}
            onChange={(event, newValue) => {
              if (newValue) {
                addTagToRecipe(newValue);
                setSelectedTag(null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search and select a tag..."
                size="small"
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <TagChip tag={option} />
                <Typography sx={{ ml: 1 }}>{option.name}</Typography>
              </Box>
            )}
            disabled={loading || unassignedTags.length === 0}
          />
          {unassignedTags.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              All available tags are already assigned to this recipe
            </Typography>
          )}
        </Box>

        {/* Create New Tag */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Create New Tag
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={8}>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter new tag name..."
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newTagName.trim()) {
                    createNewTag();
                  }
                }}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={2}>
              <FormControl size="small" fullWidth>
                <Select
                  value={newTagColor}
                  onChange={(e) => setNewTagColor(e.target.value)}
                  disabled={loading}
                  renderValue={(value) => (
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        backgroundColor: value,
                        borderRadius: '50%',
                        border: '1px solid #ccc'
                      }}
                    />
                  )}
                >
                  {PREDEFINED_COLORS.map((color) => (
                    <MenuItem key={color} value={color}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            backgroundColor: color,
                            borderRadius: '50%',
                            border: '1px solid #ccc',
                            mr: 1
                          }}
                        />
                        {color}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={2}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={createNewTag}
                disabled={loading || !newTagName.trim()}
                fullWidth
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
